import { createFileRoute, SearchParamError } from '@tanstack/react-router';
import { Option, Schema } from 'effect';
import { EvidenceInspector } from '../../../../onboarding/components/review/evidence-inspector/index.tsx';
import {
  onboardingReadsRefusal,
  useEvidenceRun,
} from '../../../../onboarding/queries/use-onboarding-query.ts';
import { onboardingRefusedNotFound } from '@finnest/power-admin-client';
import { CustomerId, RunId } from '@finnest/power-admin-contracts';
import { ErrorState, LoadingState, failedAtOf } from '../../../../ui/states.tsx';

const evidenceSearchSchema = Schema.toStandardSchemaV1(
  Schema.Struct({
    customerId: CustomerId,
  }),
);

// `validateSearch` runs during route matching, so a URL without a usable
// `?customerId=` never reaches the component: TanStack rejects the match with a
// `SearchParamError` whose message is the raw schema issue text. `errorComponent`
// is what keeps that off the screen — without it the match error falls through
// to the router's default error boundary and the operator sees the issue dump.
//
// The evidence-run read is scoped to a customer (the Power onboarding API
// returns not-found for a cross-customer run), so the customer id travels in the
// query string via `validateSearch`. TODO(#2545): source customerId from the
// authenticated admin session/customer context once the admin auth surface
// lands, instead of trusting the query param.
//
// Declared inline: the route plugin splits `component` and `errorComponent` into lazy bindings
// it declares after the options, so an options object declared on its own reads them before they
// exist in dev. The route test mounts `Route.options` (real `validateSearch`, real component,
// real error boundary) in a memory router instead of re-declaring them.
export const Route = createFileRoute('/onboarding/evidence/{$phaseDir}/{$runId}')({
  validateSearch: evidenceSearchSchema,
  component: EvidenceRunPage,
  errorComponent: EvidenceRunErrorPage,
});

// Path params come from the match itself: the route only matches when both
// `{$phaseDir}` and `{$runId}` segments are present and non-empty, and search is
// already validated by `validateSearch`, so neither needs a second parse here.
function EvidenceRunPage(): JSX.Element {
  const { phaseDir, runId } = Route.useParams();
  const { customerId } = Route.useSearch();
  return (
    <EvidenceRunLoader customerId={customerId} phaseDir={phaseDir} runId={RunId.make(runId)} />
  );
}

// A `SearchParamError` is the URL being wrong (missing/empty `customerId`), which
// is a different thing from the run itself failing to load, so the two keep
// distinct copy rather than collapsing into one message.
function EvidenceRunErrorPage({ error }: { readonly error: Error }): JSX.Element {
  if (error instanceof SearchParamError) {
    return <InvalidEvidenceRunUrlPage />;
  }
  return <MissingEvidenceRunPage />;
}

function InvalidEvidenceRunUrlPage(): JSX.Element {
  return (
    <section className="admin-page">
      <p>URL de execução de evidência inválida.</p>
    </section>
  );
}

function MissingEvidenceRunPage(): JSX.Element {
  return (
    <section className="admin-page">
      <header className="admin-page-header">
        <span className="admin-eyebrow">Onboarding</span>
        <h1>Execução de evidência não encontrada</h1>
        <p>
          Não foi possível carregar a execução de evidência. Ela pode não existir ou a URL pode
          estar incorreta.
        </p>
      </header>
      <a href="/onboarding">Abrir painel de onboarding</a>
    </section>
  );
}

type EvidenceRunLoaderProps = {
  readonly customerId: CustomerId;
  readonly phaseDir: string;
  readonly runId: RunId;
};

const RUN_SUBJECT = 'a execução de evidência';

function EvidenceRunLoader({ customerId, phaseDir, runId }: EvidenceRunLoaderProps): JSX.Element {
  const runQuery = useEvidenceRun(customerId, phaseDir, runId);

  if (runQuery.status === 'pending') {
    return (
      <section className="admin-page">
        <LoadingState label={RUN_SUBJECT} layout="page" />
      </section>
    );
  }

  // Power says the run does not exist: reading it again cannot help, so no retry is offered.
  if (runQuery.status === 'error' && onboardingRefusedNotFound(runQuery.error)) {
    return <MissingEvidenceRunPage />;
  }

  if (runQuery.status === 'error') {
    return (
      <section className="admin-page">
        <ErrorState
          onRetry={() => {
            void runQuery.refetch();
          }}
          retrying={runQuery.isFetching}
          subject={RUN_SUBJECT}
          failedAt={failedAtOf([runQuery])}
          reason={Option.none()}
          refusal={onboardingReadsRefusal([runQuery])}
          traceable
        />
      </section>
    );
  }

  return <EvidenceInspector run={runQuery.data} />;
}
